// ImageMemeSlideShow.tsx
'use client'
import * as React from 'react'

import { useImagePreview } from '@/hooks/use-image-preview'

import { ClickableImage } from '../common/clickable-image'
import { ImagePreviewModal } from '../common/image-preview-modal'
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../ui/carousel'

interface ImageSlideShowProps {
  slides: { src: string; alt: string }[]
}

/** ------------------------------------------------------------------ *
 * Small helper – returns width / height once the file is in cache.
 * ------------------------------------------------------------------ */
function useIntrinsicSize(src: string) {
  const [size, setSize] = React.useState<{ w: number; h: number } | null>(null)

  React.useEffect(() => {
    let active = true
    const img = new window.Image()
    img.onload = () => {
      if (active) setSize({ w: img.naturalWidth, h: img.naturalHeight })
    }
    img.src = src
    return () => {
      active = false
    }
  }, [src])

  return size
}

/** ------------------------------------------------------------------ *
 * Adaptive wrapper that swaps from a safe 1:1 skeleton to
 * the real aspect-ratio as soon as we know it.
 * ------------------------------------------------------------------ */
function AdaptiveImage({ src, alt, onImageClick }: { src: string; alt: string; onImageClick: () => void }) {
  const size = useIntrinsicSize(src)

  // Start with square (1/1) so layout is stable, then
  // switch to the true ratio → e.g. 16/9 = 1.777…
  const ratio = size ? size.w / size.h : 1

  return (
    <div
      style={{ aspectRatio: ratio }}
      className="relative w-full bg-inherit flex items-center justify-center overflow-hidden"
    >
      <ClickableImage
        src={src || '/images/placeholder.png'}
        alt={alt}
        onClick={onImageClick}
        fill
        priority
        className="object-contain select-none"
        placeholder="blur"
        blurDataURL="/images/placeholder_blur.png"
        showOverlay={false}
      />
    </div>
  )
}

export default function ImageMemeSlideShow({ slides }: ImageSlideShowProps) {
  const { isOpen, currentImage, openPreview, closePreview } = useImagePreview()

  const handleImageClick = (src: string, alt: string) => {
    openPreview(src, alt)
  }

  return (
    <>
      <Carousel className="w-full relative group">
        <CarouselContent className="relative -ml-0">
          {slides.map(image => (
            <CarouselItem key={image.alt} className="pl-0 basis-full flex items-center justify-center">
              <AdaptiveImage
                src={image.src}
                alt={image.alt}
                onImageClick={() => handleImageClick(image.src, image.alt)}
              />
            </CarouselItem>
          ))}
        </CarouselContent>

        {slides.length > 1 && (
          <div className="group-hover:block hidden group:transition-all duration-300 ease-in-out">
            <CarouselPrevious className="absolute top-1/2 left-2 transform -translate-y-1/2 z-10" />
            <CarouselNext className="absolute top-1/2 right-2 transform -translate-y-1/2 z-10" />
          </div>
        )}
      </Carousel>

      {currentImage && (
        <ImagePreviewModal
          src={currentImage.src}
          alt={currentImage.alt}
          isOpen={isOpen}
          onClose={closePreview}
          showDownload={false}
        />
      )}
    </>
  )
}
